<template>
  <Page>
    <template #title>Справочники</template>
    <BaseNavigation class="mb-6" :items="menu" />

    <PageContent>
      <BaseTable
        :columns="columns"
        :items="items"
        :is-loading="isLoading"
        @row-click="onRowClick"
      >
        <template #header-created_at="column">
          <div class="flex flex-row items-center">
            <div class="mr-2">{{ column.name }}</div>
            <BaseButton
              variant="none"
              size="none"
              @click="changeFilterOrder('created_at')"
            >
              <BaseIcon
                :class="{'text-black': createdAtSortApplied}"
                :name="createdAtSortIcon"
                width="12"
                height="12"
              />
            </BaseButton>
          </div>
        </template>
        <template #header-id="column">
          <div class="flex flex-row items-center">
            <div class="mr-2">{{ column.name }}</div>
            <BaseButton
              variant="none"
              size="none"
              @click="changeFilterOrder('id')"
            >
              <BaseIcon
                :class="{'text-black': idSortApplied}"
                :name="idSortIcon"
                width="12"
                height="12"
              />
            </BaseButton>
          </div>
        </template>

        <template #header-mintrud_accepted="column">
          {{ column.name }}
          <BaseSelect
            label="name"
            v-model="filter.mintrudAccepted"
            :options="operatorStatuses"
            :clearable="true"
            @input="changeFilter"
          />
        </template>
        <template #header-pfr_accepted="column">
          {{ column.name }}
          <BaseSelect
            label="name"
            v-model="filter.pfrAccepted"
            :options="operatorStatuses"
            :clearable="true"
            @input="changeFilter"
          />
        </template>
        <template #header-status="column">
          {{ column.name }}
          <LazySelect
            class="filter-select mt-1"
            label="name"
            v-model="filter.status"
            :clearable="true"
            :source="loadDictionaryReqStatusesSelect"
            @input="changeFilter"
          />
        </template>

        <template #column-status="item">
          <div class="flex flex-row items-center justify-between relative">
            {{ item.status }}
            <div class="absolute right-0 mt-1">
              <BaseButton
                v-if="item.answer && item.answer.length"
                variant="none"
                size="none"
                @click.stop="showAnswer(item.answer)"
              >
                <BaseIcon name="info" width="26" height="26" />
              </BaseButton>
            </div>
          </div>
        </template>
        <template #column-mintrud_accepted="item">
          <span :class="getAcceptedClasses(item.mintrud_accepted, 'mintrud')">
            {{ getAcceptedName(item.mintrud_accepted) }}
          </span>
        </template>
        <template #column-pfr_accepted="item">
          <span :class="getAcceptedClasses(item.pfr_accepted, 'pfr')">
            {{ getAcceptedName(item.pfr_accepted) }}
          </span>
        </template>
      </BaseTable>

      <BasePagination
        class="my-6"
        :pagination="pagination"
        @paginate="onPaginate"
      />
    </PageContent>
  </Page>
</template>

<script>
  import isEqual from 'lodash/isEqual';
  import Client from '@/classes/Api/Client';

  import {mapGetters, mapState} from 'vuex';

  import Page from '@/components/layouts/main/page';
  import BaseIcon from '@/components/global/base-icon';
  import BaseTable from '@/components/global/base-table';
  import BaseButton from '@/components/global/base-button';
  import BaseSelect from '@/components/global/base-select';
  import LazySelect from '@/components/global/lazy-select';
  import BaseNavigation from '@/components/global/base-navigation';
  import BasePagination from '@/components/global/base-pagination';
  import PageContent from '@/components/layouts/main/page/PageContent';

  import {
    getOperatorStatusName,
    getOperatorStatusById,
    getOperatorStatuses,
  } from '@/utils/dictionaryOperatorStatus';
  import {getCategoryNameById} from '@/utils/dictionaryCategories';

  export default {
    name: 'OperatorDictionaryListPage',

    components: {
      Page,
      BaseIcon,
      BaseTable,
      BaseButton,
      BaseSelect,
      LazySelect,
      PageContent,
      BaseNavigation,
      BasePagination,
    },

    data() {
      return {
        isLoading: false,

        filter: {
          sort: null,
          order: null,
          status: null,
          pfrAccepted: null,
          mintrudAccepted: null,
        },

        menu: [
          {
            name: 'Заявки',
            link: 'operator.dictionary.list',
          },
          {
            name: 'Условия',
            link: 'operator.condition.list',
          },
        ],

        columns: [
          {
            name: 'Дата',
            slug: 'created_at',
            classes: ['cursor-pointer'],
            headClasses: ['col-created_at'],
          },
          {
            name: 'Номер',
            slug: 'id',
            classes: ['cursor-pointer'],
            headClasses: ['col-id'],
          },
          {
            name: 'ЖСт',
            slug: 'life_situation',
            classes: ['cursor-pointer'],
            headClasses: ['col-life_situation'],
          },
          {
            name: 'Значение',
            slug: 'value',
            classes: ['cursor-pointer'],
            headClasses: ['col-value'],
          },
          {
            name: 'Минтруд России',
            slug: 'mintrud_accepted',
            classes: ['cursor-pointer'],
            headClasses: ['col-mintrud_accepted'],
          },
          {
            name: 'ПФР',
            slug: 'pfr_accepted',
            classes: ['cursor-pointer'],
            headClasses: ['col-pfr_accepted'],
          },
          {
            name: 'Статус',
            slug: 'status',
            classes: ['cursor-pointer'],
            headClasses: ['col-status'],
          },
        ],

        operatorStatuses: getOperatorStatuses(),
      };
    },

    computed: {
      ...mapGetters('Auth', ['hasRole']),
      ...mapState('DictionaryRequestList', ['requests', 'pagination']),

      items() {
        let result = [];
        for (let i in this.requests) {
          let request = Object.assign({}, this.requests[i]);
          request.dictionary = getCategoryNameById(request.dictionary);
          result.push(request);
        }

        return result;
      },
      idSortApplied() {
        return this.filter.order === 'id';
      },
      idSortIcon() {
        if (this.filter.order !== 'id') {
          return 'sort-desc';
        }
        return `sort-${this.filter.sort}`;
      },
      createdAtSortApplied() {
        return this.filter.order === 'created_at';
      },
      createdAtSortIcon() {
        if (this.filter.order !== 'created_at') {
          return 'sort-desc';
        }
        return `sort-${this.filter.sort}`;
      },
    },

    created() {
      this.isLoading = true;

      Client.getOperatorDictionaryRequestFilter(this.getQueryFilter()).then(
        (response) => {
          this.bindFilter(response.data);
        }
      );

      this.$store
        .dispatch(
          'DictionaryRequestList/loadForOperator',
          this.getQueryFilter()
        )
        .finally(() => {
          this.isLoading = false;
        });
    },

    destroyed() {
      this.$store.dispatch('DictionaryRequestList/clear');
    },

    beforeRouteUpdate(to, from, next) {
      this.$store.dispatch(
        'DictionaryRequestList/loadForOperator',
        this.getQueryFilter(to.query)
      );
      next();
    },

    methods: {
      getAcceptedName(val) {
        return getOperatorStatusName(val);
      },

      getAcceptedClasses(val, role) {
        let flag = this.hasRole(role) && val === null;
        return {
          'px-1 py-1 bg-bananaYellow rounded text-white': flag,
        };
      },

      loadDictionaryReqStatusesSelect() {
        return Client.getDictionaryReqStatuses().then((response) => {
          return response.data;
        });
      },

      bindFilter(data) {
        this.filter = {
          sort: data.sort,
          order: data.order,
          status: data.status,
          pfrAccepted: getOperatorStatusById(data.pfr_accepted),
          mintrudAccepted: getOperatorStatusById(data.mintrud_accepted),
        };
      },

      changeFilter() {
        let curQuery = this.$route.query;
        let newQuery = Object.assign({}, curQuery, {
          page: 1,
          sort: this.filter.sort || null,
          order: this.filter.order || null,
          status_id: this.filter.status?.id || null,
          pfr_accepted: this.filter.pfrAccepted?.id || null,
          mintrud_accepted: this.filter.mintrudAccepted?.id || null,
        });

        if (!isEqual(curQuery, newQuery)) {
          this.$router.push({
            query: newQuery,
          });
        }
      },

      changeFilterOrder(field) {
        let sort = 'desc';
        if (field === this.filter.order) {
          sort = this.filter.sort === 'desc' ? 'asc' : 'desc';
        } else {
          sort = 'asc';
        }

        Object.assign(this.filter, {
          order: field,
          sort: sort,
        });

        this.changeFilter();
      },

      getQueryFilter(query) {
        query = query || this.$route.query;
        return {
          page: query.page || 1,
          sort: query.sort || 'desc',
          order: query.order || 'id',
          status_id: query.status_id || null,
          pfr_accepted: query.pfr_accepted || null,
          mintrud_accepted: query.mintrud_accepted || null,
        };
      },

      onPaginate(page) {
        this.$router.push({
          name: 'operator.dictionary',
          query: Object.assign(this.getQueryFilter(), {
            page: page,
          }),
        });
      },

      onRowClick(item) {
        const isTextHighlighting =
          window.getSelection().toString().trim() !== '';

        if (!isTextHighlighting) {
          this.$router.push(this.onDetailPage(item.id));
        }
      },

      onDetailPage(id) {
        return {
          name: 'operator.dictionary.detail',
          params: {
            id: id,
          },
          query: this.getQueryFilter(),
        };
      },

      showAnswer(answer) {
        this.$store.commit('Notification/showAlert', {
          title: 'Ответ',
          message: answer,
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  ::v-deep {
    .col-created_at {
      vertical-align: top;
      width: rem(100px);
    }
    .col-id {
      vertical-align: top;
      width: rem(50px);
    }
    .col-life_situation {
      vertical-align: top;
      width: rem(100px);
    }
    .col-value {
      vertical-align: top;
    }
    .col-mintrud_accepted {
      width: rem(220px);
    }
    .col-pfr_accepted {
      width: rem(220px);
    }
    .col-status {
      width: rem(220px);
    }
  }
</style>
